import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { GenerationProgress } from '@/components/shared/GenerationProgress';
import { AccessibleLoader } from '@/components/shared/AccessibleLoader';
import { ChatPanel } from '@/components/builder/ChatPanel';
import { DeckFrame } from '@/components/builder/DeckFrame';
import { DeckFullscreen } from '@/components/builder/DeckFullscreen';
import { DeckShareDialog } from '@/components/builder/DeckShareDialog';
import { DeckSlideList } from '@/components/builder/DeckSlideList';
import { DeckToolbar } from '@/components/builder/DeckToolbar';
import { VersionsPanel } from '@/components/builder/VersionsPanel';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import { exportDeckToPdf } from '@/utils/deck-pdf';
import {
  builderApi,
  isDeckContent,
  missingArtifactOf,
  type Build,
  type BuilderMessage,
  type BuildVersionMeta,
  type DeckSchema,
  type DeckShareStatus,
  type DeckSlide,
} from '@/utils/api-pb';

const GENERATION_ESTIMATE_SECONDS = 60;

/** Deck chat suggestions — order and slide wording, not page sections. */
const DECK_SUGGESTIONS = ['shorter', 'order', 'tone', 'darker'] as const;

/** The deck document renders visible slides only, so the two indexes differ. */
function toVisibleIndex(slides: DeckSlide[], index: number): number {
  return slides.slice(0, index).filter((slide) => slide.visible).length;
}

function fromVisibleIndex(slides: DeckSlide[], visibleIndex: number): number {
  let seen = 0;
  for (let i = 0; i < slides.length; i += 1) {
    if (!slides[i]?.visible) continue;
    if (seen === visibleIndex) return i;
    seen += 1;
  }
  return 0;
}

/**
 * The deck editor.
 *
 * Shares the builder's whole backend with the landing editor — same versions, same
 * ops, same edit chat — and differs in what it puts on screen: a 16/9 frame instead of
 * a scrolling page, a slide strip instead of viewport switches, and no lead form or
 * address anywhere, because a deck has neither.
 */
export default function DeckEditorPage() {
  const { t, i18n } = useTranslation('builder');
  const { buildId = '' } = useParams();
  const navigate = useLocalizedNavigate();

  const [build, setBuild] = useState<Build | null>(null);
  const [html, setHtml] = useState('');
  const [content, setContent] = useState<DeckSchema | null>(null);
  const [version, setVersion] = useState<number | null>(null);
  const [versions, setVersions] = useState<BuildVersionMeta[]>([]);
  const [messages, setMessages] = useState<BuilderMessage[]>([]);
  const [slideIndex, setSlideIndex] = useState(0);
  const [showVersions, setShowVersions] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [share, setShare] = useState<DeckShareStatus | null>(null);
  const [isLoadingShare, setIsLoadingShare] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isChatting, setIsChatting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const slides = useMemo(() => content?.slides ?? [], [content]);
  const visibleCount = useMemo(() => slides.filter((slide) => slide.visible).length, [slides]);
  const activeSlide = slides[slideIndex] ?? null;
  const missingSource = activeSlide ? missingArtifactOf(activeSlide) : null;

  const refreshPreview = useCallback(async () => {
    const [preview, data] = await Promise.all([builderApi.preview(buildId), builderApi.get(buildId)]);
    setHtml(preview.html);
    setVersion(preview.version);
    setContent(isDeckContent(data.content) ? data.content : null);
  }, [buildId]);

  const refreshVersions = useCallback(async () => {
    setVersions(await builderApi.listVersions(buildId));
  }, [buildId]);

  const refreshMessages = useCallback(async () => {
    setMessages(await builderApi.listMessages(buildId));
  }, [buildId]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      try {
        const data = await builderApi.get(buildId);
        if (cancelled) return;
        setBuild(data.build);
        setContent(isDeckContent(data.content) ? data.content : null);

        if (data.currentVersion) {
          await Promise.all([refreshPreview(), refreshVersions(), refreshMessages()]);
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(error instanceof Error ? error.message : t('toast.error'));
          navigate('/builder');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [buildId, refreshPreview, refreshVersions, refreshMessages, navigate, t]);

  /**
   * A change described in words.
   *
   * The reply is always shown, including when nothing changed: a model refusing to
   * invent traction numbers is exactly the answer the owner has to read.
   */
  const handleChat = useCallback(
    async (message: string) => {
      setIsChatting(true);
      try {
        const result = await builderApi.chat(buildId, message, i18n.language as 'ru' | 'en');
        await refreshMessages();
        if (result.changed) await Promise.all([refreshPreview(), refreshVersions()]);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : t('toast.error'));
        await refreshMessages().catch(() => {});
      } finally {
        setIsChatting(false);
      }
    },
    [buildId, i18n.language, refreshMessages, refreshPreview, refreshVersions, t],
  );

  async function handleGenerate() {
    if (!build) return;
    setIsGenerating(true);
    try {
      const result = await builderApi.generate(
        buildId,
        build.brief as Record<string, unknown>,
        i18n.language as 'ru' | 'en',
      );
      await Promise.all([refreshPreview(), refreshVersions()]);
      setSlideIndex(0);
      toast[result.degraded ? 'warning' : 'success'](
        result.degraded ? t('deck.toast.generatedDegraded') : t('deck.toast.generated'),
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toast.error'));
    } finally {
      setIsGenerating(false);
    }
  }

  /**
   * Saves the deck as a PDF.
   *
   * Rendered from the same HTML the preview shows, in the browser: a headless Chromium
   * does not fit the staging VPS (spec decision #6). It takes a few seconds, so the
   * button says so rather than looking dead.
   */
  async function handleExportPdf() {
    if (!build || !html) return;
    setIsExporting(true);
    try {
      await exportDeckToPdf({ html, name: build.name });
      toast.success(t('deck.toast.exported'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toast.error'));
    } finally {
      setIsExporting(false);
    }
  }

  /** Статус ссылки читается при открытии панели — большинство сессий её не трогает. */
  async function openShare() {
    setShowShare(true);
    setIsLoadingShare(true);
    try {
      setShare(await builderApi.shareStatus(buildId));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toast.error'));
      setShowShare(false);
    } finally {
      setIsLoadingShare(false);
    }
  }

  async function handleEnableShare() {
    setIsBusy(true);
    try {
      const result = await builderApi.enableShare(buildId);
      setShare({ enabled: true, url: result.url, createdAt: result.createdAt });
      toast.success(t('deck.toast.shared'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toast.error'));
    } finally {
      setIsBusy(false);
    }
  }

  async function handleRevokeShare() {
    setIsBusy(true);
    try {
      await builderApi.revokeShare(buildId);
      setShare({ enabled: false, url: null, createdAt: null });
      toast.success(t('deck.toast.shareRevoked'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toast.error'));
    } finally {
      setIsBusy(false);
    }
  }

  async function handleRestore(target: number) {
    setIsBusy(true);
    try {
      await builderApi.restore(buildId, target);
      await Promise.all([refreshPreview(), refreshVersions()]);
      toast.success(t('toast.restored', { n: target }));
      setShowVersions(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toast.error'));
    } finally {
      setIsBusy(false);
    }
  }

  if (isLoading) return <AccessibleLoader label={t('loading', { ns: 'common' })} />;
  if (!build) return null;

  if (isGenerating) {
    return (
      <GenerationProgress
        title={t('deck.generating.title')}
        message={t('deck.generating.message')}
        estimatedDuration={GENERATION_ESTIMATE_SECONDS}
      />
    );
  }

  if (isFullscreen && html) {
    return (
      <DeckFullscreen
        html={html}
        slideCount={visibleCount}
        initialIndex={toVisibleIndex(slides, slideIndex)}
        onExit={() => setIsFullscreen(false)}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-[12px]">
      <DeckToolbar
        build={build}
        version={version}
        slideCount={slides.length}
        isBusy={isBusy || isChatting}
        hasContent={Boolean(html)}
        isExporting={isExporting}
        onBack={() => navigate('/builder')}
        onOpenVersions={() => setShowVersions(true)}
        onFullscreen={() => setIsFullscreen(true)}
        onRegenerate={handleGenerate}
        onExportPdf={handleExportPdf}
        onShare={openShare}
      />

      {html ? (
        <div className="flex min-h-0 flex-1 flex-col gap-[12px] lg:flex-row">
          <DeckSlideList
            slides={slides}
            activeIndex={slideIndex}
            onSelect={(index) => setSlideIndex(index)}
          />

          <div className="flex min-h-0 flex-1 flex-col gap-[8px]">
            <p className="font-segoe text-[13px] text-[color:var(--grey-text,#969696)]">
              {t('deck.editor.hint')}
            </p>

            <div className="flex min-h-0 flex-1 items-start justify-center">
              <DeckFrame
                html={html}
                slideIndex={toVisibleIndex(slides, slideIndex)}
                onSlideChange={(visibleIndex) => setSlideIndex(fromVisibleIndex(slides, visibleIndex))}
                busy={isBusy || isChatting}
              />
            </div>

            {/* Слайд без материала не показывается в самой презентации, поэтому
                сказать о нём можно только здесь — иначе выбор в списке выглядит
                сломанным: кликнул, а кадр не сменился. */}
            {missingSource && (
              <div className="rounded-[16px] bg-[var(--error-soft,#ffeded)] px-[16px] py-[12px]">
                <p className="font-manrope text-[14px] font-semibold text-[color:var(--error-strong,#b3261e)]">
                  {t('deck.missing.title', {
                    source: t(`deck.missing.source.${missingSource}`, { defaultValue: missingSource }),
                  })}
                </p>
                <p className="mt-[2px] font-segoe text-[13px] text-[color:var(--black-text,#0d0d0d)]">
                  {t('deck.missing.text')}
                </p>
              </div>
            )}
          </div>

          <ChatPanel
            messages={messages}
            isSending={isChatting}
            disabled={isBusy}
            onSend={handleChat}
            i18nPrefix="deck.chat"
            suggestionKeys={DECK_SUGGESTIONS}
          />
        </div>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-[12px] text-center">
          <h2 className="font-manrope text-[20px] font-bold text-[color:var(--black-text,#0d0d0d)]">
            {t('deck.editor.empty.title')}
          </h2>
          <p className="font-segoe text-[15px] text-[color:var(--grey-text,#565858)]">
            {t('deck.editor.empty.text')}
          </p>
          <Button variant="brand" size="brand" onClick={handleGenerate}>
            {t('deck.editor.empty.action')}
          </Button>
        </div>
      )}

      <Dialog open={showShare} onOpenChange={setShowShare}>
        <DialogContent className="max-h-[90vh] max-w-[520px] overflow-y-auto p-6 sm:p-8">
          <DialogHeader className="text-center">
            <DialogTitle className="text-center font-manrope text-[24px] font-bold">
              {t('deck.share.title')}
            </DialogTitle>
          </DialogHeader>
          <DeckShareDialog
            status={share}
            isLoading={isLoadingShare}
            isBusy={isBusy}
            onEnable={handleEnableShare}
            onRevoke={handleRevokeShare}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={showVersions} onOpenChange={setShowVersions}>
        <DialogContent className="max-h-[90vh] max-w-[520px] overflow-y-auto p-6 sm:p-8">
          <DialogHeader className="text-center">
            <DialogTitle className="text-center font-manrope text-[24px] font-bold">
              {t('versions.title')}
            </DialogTitle>
          </DialogHeader>
          <VersionsPanel
            versions={versions}
            currentVersion={version}
            isBusy={isBusy}
            onRestore={handleRestore}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
